import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { teamsApi } from '../api/teams';
import { useAuth } from './AuthContext';
import { PUBLISHING_IDENTITY_KEY as STORAGE_KEY, readPublishingIdentity } from '../utils/publishingIdentity';
const DEFAULT_IDENTITY = {
  activeType: 'user', activeTeam: null, availableTeam: null, loading: false,
  refresh: async () => null, switchToUser: () => {}, switchToTeam: () => {},
};
const PublishingIdentityContext = createContext(DEFAULT_IDENTITY);

export function PublishingIdentityProvider({ children }) {
  const { currentUserId, loading: authLoading } = useAuth();
  const accountRef = useRef(currentUserId);
  accountRef.current = currentUserId;
  const [team, setTeam] = useState(null);
  const [activeType, setActiveType] = useState('user');
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!currentUserId) {
      setTeam(null); setActiveType('user'); localStorage.removeItem(STORAGE_KEY); return null;
    }
    setLoading(true);
    try {
      const mine = await teamsApi.mine();
      if (accountRef.current !== currentUserId) return null;
      const eligible = mine?.myPermissions?.publishAsTeam === true ? mine : null;
      setTeam(eligible);
      const selected = readPublishingIdentity();
      const keepTeam = eligible && selected?.type === 'team'
        && selected?.teamId === eligible.id && selected?.userId === currentUserId;
      setActiveType(keepTeam ? 'team' : 'user');
      if (!keepTeam) localStorage.removeItem(STORAGE_KEY);
      return mine;
    } catch (_) {
      if (accountRef.current !== currentUserId) return null;
      setTeam(null); setActiveType('user'); localStorage.removeItem(STORAGE_KEY); return null;
    } finally { if (accountRef.current === currentUserId) setLoading(false); }
  }, [currentUserId]);

  useEffect(() => {
    if (authLoading) return;
    setTeam(null);
    setActiveType('user');
    setLoading(false);
    const selected = readPublishingIdentity();
    // Only restore a team identity that this account previously selected.
    // Personal accounts discover their team when opening the account switcher.
    if (currentUserId && selected?.type === 'team' && selected?.userId === currentUserId) {
      refresh();
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [authLoading, currentUserId, refresh]);

  const switchToUser = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setActiveType('user');
  }, []);

  const switchToTeam = useCallback(() => {
    if (!team || !currentUserId) return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ type: 'team', teamId: team.id, userId: currentUserId }));
    setActiveType('team');
  }, [currentUserId, team]);

  const value = useMemo(() => ({
    activeType, activeTeam: activeType === 'team' ? team : null, availableTeam: team,
    loading, refresh, switchToUser, switchToTeam,
  }), [activeType, loading, refresh, switchToTeam, switchToUser, team]);
  return <PublishingIdentityContext.Provider value={value}>{children}</PublishingIdentityContext.Provider>;
}

export const usePublishingIdentity = () => {
  return useContext(PublishingIdentityContext);
};
